/**
 * Utilidades para manejo de fechas en zona horaria local (Perú UTC-5)
 *
 * IMPORTANTE: Evitar usar toISOString() ya que convierte a UTC y puede causar
 * desplazamientos de fecha. Estas funciones usan la zona horaria local del navegador.
 */

/**
 * Convierte una fecha a formato YYYY-MM-DD en zona horaria local
 * @param {Date} fecha - Objeto Date a convertir
 * @returns {string} Fecha en formato YYYY-MM-DD
 */
export const formatearFechaLocal = (fecha) => {
  const year = fecha.getFullYear();
  const month = String(fecha.getMonth() + 1).padStart(2, '0');
  const day = String(fecha.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Convierte una fecha a formato YYYY-MM-DD HH:mm:ss en zona horaria local
 * @param {Date} fecha - Objeto Date a convertir
 * @returns {string} Fecha y hora en formato YYYY-MM-DD HH:mm:ss
 */
export const formatearFechaHoraLocal = (fecha) => {
  const year = fecha.getFullYear();
  const month = String(fecha.getMonth() + 1).padStart(2, '0');
  const day = String(fecha.getDate()).padStart(2, '0');
  const hours = String(fecha.getHours()).padStart(2, '0');
  const minutes = String(fecha.getMinutes()).padStart(2, '0');
  const seconds = String(fecha.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * Convierte una fecha a formato YYYY-MM-DDTHH:mm para inputs datetime-local
 * @param {Date} fecha - Objeto Date a convertir
 * @returns {string} Fecha en formato para input datetime-local
 */
export const formatearParaDatetimeLocal = (fecha) => {
  const year = fecha.getFullYear();
  const month = String(fecha.getMonth() + 1).padStart(2, '0');
  const day = String(fecha.getDate()).padStart(2, '0');
  const hours = String(fecha.getHours()).padStart(2, '0');
  const minutes = String(fecha.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Obtiene la fecha de hoy en formato YYYY-MM-DD (zona horaria local)
 * @returns {string} Fecha de hoy
 */
export const obtenerFechaHoy = () => {
  return formatearFechaLocal(new Date());
};

/**
 * Obtiene la fecha de ayer en formato YYYY-MM-DD (zona horaria local)
 * @returns {string} Fecha de ayer
 */
export const obtenerFechaAyer = () => {
  const ayer = new Date();
  ayer.setDate(ayer.getDate() - 1);
  return formatearFechaLocal(ayer);
};

/**
 * Convierte un string de fecha SQL (puede incluir hora) a formato YYYY-MM-DD local
 * @param {string} fechaSQL - Fecha en formato SQL (ej: "2025-12-27" o "2025-12-27T10:30:00")
 * @returns {string} Fecha en formato YYYY-MM-DD local
 */
export const sqlAFechaLocal = (fechaSQL) => {
  if (!fechaSQL) return '';

  // Si la fecha ya viene en formato YYYY-MM-DD sin hora, retornarla directamente
  if (/^\d{4}-\d{2}-\d{2}$/.test(fechaSQL)) {
    return fechaSQL;
  }

  // Si viene con hora, parsear y convertir a local
  const fecha = new Date(fechaSQL);
  return formatearFechaLocal(fecha);
};

/**
 * Calcula el inicio de la semana actual (domingo = 0)
 * @returns {string} Fecha del inicio de semana en formato YYYY-MM-DD
 */
export const obtenerInicioSemana = () => {
  const hoy = new Date();
  const inicioSemana = new Date(hoy);
  inicioSemana.setDate(hoy.getDate() - hoy.getDay());
  return formatearFechaLocal(inicioSemana);
};

/**
 * Calcula el inicio del mes actual
 * @returns {string} Fecha del primer día del mes en formato YYYY-MM-DD
 */
export const obtenerInicioMes = () => {
  const hoy = new Date();
  const inicioMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
  return formatearFechaLocal(inicioMes);
};

/**
 * Convierte un string ISO a Date considerando zona horaria local
 * IMPORTANTE: Usar para fechas que vienen del backend sin desplazamiento UTC
 * @param {string} isoString - Fecha en formato ISO
 * @returns {Date} Objeto Date
 */
export const isoStringADateLocal = (isoString) => {
  if (!isoString) return null;

  // Si es solo fecha (YYYY-MM-DD), crear Date sin zona horaria
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoString)) {
    const [year, month, day] = isoString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  // Si incluye hora, parsear normalmente
  return new Date(isoString);
};

/**
 * Ejemplo de uso:
 *
 * // ❌ INCORRECTO (convierte a UTC, puede cambiar la fecha)
 * const fecha = new Date().toISOString().split('T')[0];
 *
 * // ✅ CORRECTO (mantiene zona horaria local)
 * import { formatearFechaLocal } from './utils/dateUtils';
 * const fecha = formatearFechaLocal(new Date());
 *
 * // ✅ Para obtener fechas comunes
 * import { obtenerFechaHoy, obtenerFechaAyer } from './utils/dateUtils';
 * const hoy = obtenerFechaHoy();
 * const ayer = obtenerFechaAyer();
 */
